import {
  Box,
  Button,
  MenuItem,
  Stack,
  styled,
  TextField,
  Typography,
  keyframes,
  css,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Table, Td, Th, Tr } from 'components/Table';
import dayjs from 'dayjs';
import useSettlement, { Settlement } from 'hook/useSettlement';
import React, { useEffect, useState } from 'react';
import color from 'styles/color';
// date-fns
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useMutation, { METHOD } from 'hook/useMutation';
import { API_PATH } from 'common/constants/services';
import commaNumber from 'comma-number';
import { SETTLEMENTS_STATUS } from 'model/Settlements';
import { toast } from 'react-toastify';
import { queryClient } from 'App';

const openKeyFrame = keyframes`
from{
  width:0px;
  opacity:0;
}
0% { 
  width:100vw 
}
to{
  width:100%;
  opacity:1;
}
`;
const closeKeyFrame = keyframes`
from{
  opacity:1;
  width:100vw
}
99.9% { 
  width:100vw
}
to{
  opacity:0;
  width:0
}
`;

const openModalKeyFrame = keyframes`
from{
  width:0px
}
to{
  width:50vw
}
`;
const closeModalKeyFrame = keyframes`
from{
  width:50vw
}
to{
  width:0
}
`;
export const BackgroundModal = styled('div')<{
  isOpen: boolean;
  isInit: boolean;
}>`
  width: 0;
  height: 100vh;
  position: fixed;
  top: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 10;
  display: flex;
  justify-content: flex-end;
  ${({ isOpen, isInit }) =>
    isOpen
      ? css`
          animation: ${openKeyFrame} 0.3s ease forwards;
        `
      : css`
          animation: ${closeKeyFrame} 0.3s ease forwards;
        `}
`;

const RootStyle = styled('div')<{ isOpen: boolean }>`
  width: 50%;
  height: 100%;
  right: 0;
  background: ${color.white};
  padding: 24px;
  position: relative;
  ${({ isOpen }) =>
    isOpen
      ? css`
          animation: ${openModalKeyFrame} 0.3s ease forwards;
        `
      : css`
          animation: ${closeModalKeyFrame} 0.3s ease forwards;
        `}
`;

const ModalContainer = styled('div')`
  width: calc(50vw - 48px);
`;
interface IProps {
  isOpen: boolean;
  close: () => void;
  userId: number;
  year: number;
  month: number;
}
export function CashModal({ isOpen, close, userId, year, month }: IProps) {
  const [currency, setCurrency] = useState('');
  const [init, setInit] = useState(false);
  const { data } = useSettlement(userId, year, month);
  const [fee, setFee] = useState('');
  const [date, setDate] = useState(new Date());
  useEffect(() => {
    if (data?.current_settlement_profile.commission)
      setFee(String(data.current_settlement_profile.commission * 100));
    if (data?.settled_at)
      setDate(dayjs(data?.settled_at, 'YYYY-MM-DD').toDate());
  }, [data]);

  const updateMutate = useMutation(API_PATH.COACHES_SETTLEMENTS, {
    method: METHOD.PATCH,
    onSuccess: () => {
      close();
      toast.success('저장완료');
      queryClient.invalidateQueries([API_PATH.COACHES_SETTLEMENTS]);
    },
    onError: () => {
      toast.error('저장실패');
    },
  });
  const currencies = [
    {
      value: '2022.01',
      label: '2022.01',
    },
    {
      value: '2022.01',
      label: '2022.01',
    },
    {
      value: '2022.01',
      label: '2022.01',
    },
    {
      value: '2022.01',
      label: '2022.01',
    },
  ];
  useEffect(() => {
    if (isOpen) setInit(true);
  }, [isOpen]);
  const [settlementList, setSettlementList] = useState<Settlement[]>([]);
  useEffect(() => {
    setSettlementList(data?.settlements || []);
  }, [data?.settlements]);

  const onChangeSettlement = (text: string, i: number) => {
    const temp = [...settlementList];
    temp[i].net_amount = Number(text);
    setSettlementList([...temp]);
  };

  return init ? (
    <BackgroundModal
      isOpen={isOpen}
      isInit={init}
      onClick={(event: any) => {
        close();
      }}
    >
      <RootStyle
        isOpen={isOpen}
        onClick={(event: any) => {
          event.stopPropagation();
        }}
      >
        <ModalContainer>
          <Typography fontSize={22} fontWeight={700} mb={3}>
            {data?.status === SETTLEMENTS_STATUS.DONE
              ? '정산 수정하기'
              : '정산하기'}
          </Typography>
          <Stack direction='row'>
            <Box width={120}>
              <Typography fontSize={14}>코치</Typography>
            </Box>
            <Box>
              <Typography fontSize={14} fontWeight={700}>
                {data?.coach.name}
              </Typography>
            </Box>
          </Stack>
          <Stack direction='row'>
            <Box width={120}>
              <Typography fontSize={14}>기준월</Typography>
            </Box>
            <Box>
              <Typography fontSize={14} fontWeight={700}>
                {year}년 {month}월
              </Typography>
            </Box>
          </Stack>
          <Stack direction='row' mb={4}>
            <Box width={120}>
              <Typography fontSize={14}>지급할 계좌</Typography>
            </Box>
            <Box>
              {data?.current_settlement_profile.bank_profile ? (
                <Typography fontSize={14} fontWeight={700}>
                  {`${data?.current_settlement_profile.bank_profile?.bank_name} `}
                  {
                    data?.current_settlement_profile.bank_profile
                      ?.account_number
                  }
                  {` ( ${data?.current_settlement_profile.bank_profile?.owner} )`}
                </Typography>
              ) : (
                <Typography fontSize={14} fontWeight={700}>
                  등록된 계좌가 없습니다.
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack direction='row' spacing={2} mb={4}>
            <Box flex={1}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  label='지급일'
                  inputFormat='yyyy-MM-dd'
                  value={date}
                  onChange={(e) => setDate(e || new Date())}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Box>
          </Stack>
          {settlementList.map((settlement, i) => (
            <Stack mb={4}>
              <Table>
                <Tr>
                  <Th>
                    <Typography>{settlement.challenge.name}</Typography>
                  </Th>
                  <Td>
                    <Stack direction='row' flex={1} alignItems='center'>
                      <Box flex={1}>
                        <TextField
                          value={settlement.net_amount}
                          onChange={(e) =>
                            onChangeSettlement(e.target.value, i)
                          }
                        />
                      </Box>
                      <Typography>원</Typography>
                    </Stack>
                  </Td>
                </Tr>
              </Table>
            </Stack>
          ))}
          <Stack direction='row' mb={4}>
            <Box width={188}>총 정산 금액</Box>
            <Box width={188}>
              <Typography fontSize={22} fontWeight={700}>
                {commaNumber(
                  String(
                    data?.settlements.reduce(
                      (acc: number, settlement) =>
                        acc + Number(settlement.net_amount || 0),
                      0,
                    ),
                  ),
                )}
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction='row'
            spacing={1}
            mb={3}
            style={{
              width: '50vw',
              position: 'absolute',
              marginLeft: -24,
              padding: 24,
              bottom: 0,
            }}
            flex={1}
          >
            <Box flex={1}>
              <Button
                fullWidth
                variant='outlined'
                style={{ borderColor: color.gray04 }}
                onClick={close}
              >
                취소
              </Button>
            </Box>
            <Box flex={1}>
              <Button
                fullWidth
                variant='contained'
                onClick={() => {
                  updateMutate.mutate({
                    year,
                    month,
                    coach_user_id: userId,
                    challenge_to_net_amount: settlementList.reduce(
                      (acc, curr) => ({
                        ...acc,
                        [curr.challenge.id]: curr.net_amount,
                      }),
                      {},
                    ),
                    settled_at: dayjs(date).format(),
                  });
                }}
              >
                {data?.status === SETTLEMENTS_STATUS.DONE
                  ? '저장하기'
                  : '정산 완료'}
              </Button>
            </Box>
          </Stack>
        </ModalContainer>
      </RootStyle>
    </BackgroundModal>
  ) : null;
}
